import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Siralama } from './kolon';

/**
 * Kart kipinin SIRALAMA denetimi: "Sırala" düğmesi + alttan açılan seçenek sayfası (Yerlice
 * `components/KartSirala.tsx` kalıbı). Kart kipinde kolon başlığı yoktur; başlığa basarak sıralama
 * sözleşmesi bu denetimle devralınır.
 *
 * Seçenekler KARTIN GÖSTERDİĞİ alanlarla sınırlıdır: görünmeyen bir alana göre sıralamak, ekranda
 * doğrulanamayan bir sıra üretir. Etkin seçeneğe yeniden dokunmak yönü çevirir.
 */
export interface SiralaSecenegi {
  id: string;
  etiket: string;
  /** Yeni seçilince kullanılacak yön: metin artan (A-Z), sayı ve tarih azalan. */
  varsayilanYon: 1 | -1;
}

const YON_OK = { 1: '↑', [-1]: '↓' } as const;
const YON_ADI = { 1: 'artan', [-1]: 'azalan' } as const;

export function KartSirala({ secenekler, deger, onDegis }: {
  secenekler: SiralaSecenegi[];
  /** null: verinin geldiği sıra ("Varsayılan sıra"). */
  deger: Siralama | null;
  onDegis: (s: Siralama | null) => void;
}) {
  const [acik, setAcik] = useState(false);
  const baslikId = useId();
  const sayfa = useRef<HTMLDivElement>(null);
  const tetik = useRef<HTMLButtonElement>(null);

  // Escape ile kapanır; kapanınca odak tetiğe döner (yoksa odak `body`ye düşer).
  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Çekmecenin içindeyken aynı Escape çekmeceyi de kapatmasın.
      e.stopPropagation();
      setAcik(false);
      tetik.current?.focus();
    };
    document.addEventListener('keydown', esc, true);
    sayfa.current?.querySelector<HTMLElement>('button')?.focus();
    return () => document.removeEventListener('keydown', esc, true);
  }, [acik]);

  const kapat = () => { setAcik(false); tetik.current?.focus(); };
  const sec = (s: SiralaSecenegi) => {
    onDegis(deger?.id === s.id ? { id: s.id, yon: deger.yon === 1 ? -1 : 1 } : { id: s.id, yon: s.varsayilanYon });
    kapat();
  };
  const etkin = deger ? secenekler.find((s) => s.id === deger.id) : undefined;

  return (
    <>
      <button
        ref={tetik}
        type="button"
        className={`rk-sirala-btn${etkin ? ' rk-sirala-btn--etkin' : ''}`}
        data-testid="kart-sirala-btn"
        aria-haspopup="dialog"
        aria-expanded={acik}
        onClick={() => setAcik((a) => !a)}
      >
        {etkin && deger ? `${etkin.etiket} ${YON_OK[deger.yon]}` : 'Sırala'}
      </button>

      {acik && createPortal(
        <div className="rk-sirala-katman" data-testid="kart-sirala-katman">
          <div className="rk-sirala-perde" aria-hidden="true" onClick={kapat} />
          <div ref={sayfa} className="rk-sirala-sayfa" role="dialog" aria-modal="true" aria-labelledby={baslikId}>
            <div className="rk-sirala-tutamak" aria-hidden="true" />
            <h4 className="rk-sirala-baslik" id={baslikId}>Sırala</h4>
            <ul className="rk-sirala-liste">
              {secenekler.map((s) => {
                const secili = deger?.id === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`rk-sirala-secenek${secili ? ' rk-sirala-secenek--etkin' : ''}`}
                      data-testid="kart-sirala-secenek"
                      aria-pressed={secili}
                      onClick={() => sec(s)}
                    >
                      <span>{s.etiket}</span>
                      {secili && deger && <span className="rk-sirala-yon">{YON_OK[deger.yon]} {YON_ADI[deger.yon]}</span>}
                    </button>
                  </li>
                );
              })}
              {deger && (
                <li>
                  <button
                    type="button"
                    className="rk-sirala-secenek rk-sirala-secenek--sifirla"
                    data-testid="kart-sirala-sifirla"
                    onClick={() => { onDegis(null); kapat(); }}
                  >
                    Varsayılan sıra
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
